import * as utils from '@lib/utils';
import { TDocumentDefinitions } from 'pdfmake/interfaces';

type Business = {
    name?: string;
    addressLine1?: string;
    addressLine2?: string;
    phone?: string;
};

type Contact = {
    name?: string;
    addressLine1?: string;
    addressLine2?: string;
    email?: string;
};

type LineItem = {
    name: string;
    description?: string;
    quantity: number;
    price: number;
    totalTaxes: number;
    total: number;
};

type DocumentData = {
    business?: Business;
    contact?: Contact;
    type: string;
    name: string;
    createdAt: string | Date;
    dueDate: string | Date;
    lineItems: LineItem[];
    subTotal: number;
    totalDiscount: number;
    totalTaxes: number;
    grandTotal: number;
    noteToReceiver?: string;
};

var dd = function (documentData: DocumentData, currency = ""): TDocumentDefinitions {

    const { business, contact, /* type, */ name, createdAt, /* dueDate,  lineItems,*/ subTotal, totalDiscount, grandTotal, noteToReceiver, ...otherData } = documentData;

    const formattedDocDate = utils.capitalize(utils.formatDate(documentData?.createdAt));
    const formattedDocDueDate = utils.capitalize(utils.formatDate(documentData?.dueDate));

    const formatToCurrency = (amount: number | string) => {
        // if `amount` is a number, tranform amount
        if ((typeof amount).localeCompare("number") === 0) {
            amount = parseFloat(amount as string);
            if (amount < 0) {
                amount = `-${currency} ${utils.printDecimalOf(Math.abs(amount))}`;
            } else {
                amount = `${currency} ${utils.printDecimalOf(Math.abs(amount))}`;
            }
        }
        // return the amount, transformed or not, anyway
        return amount;
    }

    return {
        // ... (Page setup and defaultStyle remain the same) ...
        pageSize: 'LETTER',

        pageMargins: [36, 36, 36, 36],

        defaultStyle: {
            font: 'Roboto',
            fontSize: 12,
            lineHeight: 1.1,
            color: '#000000',
            alignment: 'left'
        },

        content: [
            // FIRST HEADER STARTS
            {
                style: 'header',
                layout: 'noBorders',
                table: {
                    widths: ['*', 'auto'],
                    body: [
                        [
                            [
                                { text: `${documentData?.business?.name ?? ""}`/* 'Monero Jeanniton' */, style: 'businessName' },
                                { text: `${documentData?.business?.addressLine1 ?? ""}` /* '449 Broadway, St' */ },
                                { text: `${documentData?.business?.addressLine2 ?? ""}` /* 'Cambridge, MA' */ },
                                { text: `${documentData?.business?.phone ?? ""}` /* '(123) 456-7890' */ }
                            ],
                            []
                        ]
                    ]
                }
            },

            // SPACER
            { text: '', margin: [0, 15, 0, 0] },

            // SECOND HEADER
            {
                layout: 'noBorders',
                table: {
                    widths: ['50%', '50%'],
                    body: [
                        [
                            {
                                stack: [
                                    { text: documentData?.type ? `${documentData?.type?.toUpperCase()} TO: ` : `TO: ` /* 'ESTIMATE TO' */, style: 'documentTo' },
                                    { text: `${documentData?.contact?.name ?? ""}` /* 'Johnny' */, style: 'clientName' },
                                    { text: `${documentData?.contact?.addressLine1 ?? ""}` /* '796 Silver Harbour,' */ },
                                    { text: `${documentData?.contact?.addressLine2 ?? ""}`/* 'TX 79273, US' */ },
                                    { text: `${documentData?.contact?.email ?? ""}` /* 'johny@email.com' */, link: `mailto:${documentData?.contact?.email || ""}` /* 'mailto:johny@email.com' */ }
                                ]
                            },
                            {
                                stack: [
                                    { text: `${documentData?.type?.toUpperCase() ?? ""}` /* 'ESTIMATE' */, style: 'documentId' },
                                    { text: `NO: ${documentData?.name?.toUpperCase() ?? ""}` /* 'NO: EST-004' */, style: 'documentDetail' },
                                    { text: `DATE: ${formattedDocDate ?? ""}` /* 'DATE: April 03, 2025' */, style: 'documentDetail' },
                                    { text: `VAILD UNTIL: ${formattedDocDueDate ?? ""}` /* 'UNTIL: April 03, 2025' */, style: 'documentDetail' }
                                ]
                            }
                        ]
                    ]
                },
                margin: [0, 0, 0, 20]
            },

            // === LINE-ITEMS TABLE STARTS ===
            {
                layout: {
                    hLineWidth: function (i, node) {
                        // Top of the table header row
                        if (i === 0) {
                            return 0;
                        }
                        // Below the table header row
                        if (i === 1) {
                            return 0; // 3;
                        }

                        // Between table row
                        if (i > 1 && i < node.table.body.length - 3) {
                            return 1;
                        }

                        // After the subtotal table row
                        if (i === node.table.body.length - 1) {
                            return 2;
                        }

                        // Default is 0
                        return 0;
                    },
                    hLineColor: function (i, node) {
                        // After the subtotal table row
                        if (i === node.table.body.length - 1) {
                            return '#005ACE';
                        }
                        // Default
                        return '#AAAAAA';
                    },

                    vLineWidth: function (i, node) {
                        return 0;
                    },

                    // PADDING
                    paddingLeft: function (i, node) { return 8; },
                    paddingRight: function (i, node) { return 8; },
                    paddingTop: function (i, node) { return 8; },
                    paddingBottom: function (i, node) { return 8; },
                },
                table: {
                    // Should we repeat the header? **yes**
                    headerRows: 1,
                    widths: ['8%', '35%', '10%', '15%', '12%', '20%'], // 6 columns total
                    body: [
                        // Table Header Row
                        [
                            { text: '#', style: 'tableHeader' },
                            { text: 'DESCRIPTION', style: 'tableHeader' },
                            { text: 'QTY', style: 'tableHeader' },
                            { text: 'PRICE', style: 'tableHeader' },
                            { text: 'TAX', style: 'tableHeader' },
                            { text: 'TOTAL', style: ['tableHeader', 'totalAlignment'] }
                        ],

                        // LINE-ITEMS
                        ...(documentData?.lineItems || []).map(function (lineItem, index) {
                            return ([
                                { text: `${index + 1}` },
                                {
                                    stack: [
                                        { text: `${lineItem?.name}`, style: 'itemDescriptionTitle' },
                                        { text: `${lineItem?.description || ""}`, style: 'itemDescriptionDetail' }
                                    ]
                                },
                                { text: `${lineItem?.quantity}` },
                                { text: `${lineItem?.price}` },
                                { text: `${lineItem?.totalTaxes}` },
                                { text: `${lineItem.total}`, style: 'totalAlignment' }
                            ]);
                        }),

                        // SUBTOTAL
                        [
                            { text: 'SUBTOTAL', colSpan: 4, style: ['totalAlignment'] },
                            {}, {}, {},
                            { text: `${formatToCurrency(documentData?.subTotal)}`, colSpan: 2, style: ['totalAlignment'] },
                            {},
                        ],

                        // DISCOUNT
                        [
                            { text: 'DISCOUNT', colSpan: 4, style: ['totalAlignment'] },
                            {}, {}, {},
                            // Discount should be display as a negative amount
                            { text: `${formatToCurrency(-documentData?.totalDiscount)}`, colSpan: 2, style: ['totalAlignment'] },
                            {},
                        ],

                        // TAX(ES)
                        [
                            { text: 'TAX(ES)', colSpan: 4, style: ['totalAlignment'] },
                            {}, {}, {},
                            { text: `${formatToCurrency(documentData?.totalTaxes)}`, colSpan: 2, style: ['totalAlignment'] },
                            {},
                        ],

                        // GRAND TOTAL
                        [
                            { text: 'GRAND TOTAL', colSpan: 4, bold: true, style: ['tableFooter', 'totalAlignment'] },
                            {}, {}, {},
                            { text: `${formatToCurrency(documentData?.grandTotal)}`, colSpan: 2, bold: true, style: ['tableFooter'] },
                            {},
                        ]

                    ]

                },
            },
            // === LINE-ITEMS TABLE ENDS ===

            // THANK YOU SECTION STARTS
            { text: 'THANK YOU..!', style: 'thanks' },

            // NOTICE SECTION STARTS
            documentData?.noteToReceiver ?
                {
                    stack: [
                        { text: 'NOTICE:', style: ['noticeTitle'] },
                        { text: `${documentData?.noteToReceiver ?? ""}`, style: ['noticeText'] }
                    ]
                }
                :
                []
            ,

        ],

        footer: function (currentPage: number, pageCount: number) {
            // return currentPage.toString() + ' of ' + pageCount;

            // you can apply any logic and return any valid pdfmake element
            return {
                style: ['footer'],
                stack: [
                    [
                        // === PAGING / # STARTS ===
                        {
                            text: `Page ${currentPage} of ${pageCount}`,
                        },
                        // === PAGING / # ENDS ==
                    ],
                ]
            }
        },

        // ... (Styles remain the same) ...
        "styles": {
            "header": {
                "margin": [0, 0, 0, 0]
            },
            "businessName": {
                "fontSize": 16,
                "margin": [0, 0, 0, 2]
            },
            "documentTo": {
                "bold": true,
                "fontSize": 12
            },
            "clientName": {
                "margin": [0, 2, 0, 2]
            },
            "documentId": {
                "bold": true,
                "alignment": "right",
                "margin": [0, 0, 0, 5]
            },
            "documentDetail": {
                "alignment": "right"
            },
            "tableHeader": {
                "bold": true,
                "fontSize": 12,
                "color": "black",
                "fillColor": "#C4D6F4",
                "alignment": "left"
            },
            "itemDescriptionTitle": {
                "bold": false,
                "fontSize": 12,
                "margin": [0, 0, 0, 1]
            },
            "itemDescriptionDetail": {
                "fontSize": 10.8,
                "color": "#555555"
            },
            "totalAlignment": {
                "alignment": "right"
            },
            "tableFooter": {
                "alignment": "right",
                "fillColor": "#C4D6F4",
                "color": "black",
                "margin": [0, 5, 0, 0]
            },
            "thanks": {
                "fontSize": 24,
                "bold": true,
                "margin": [0, 12, 0, 40]
            },
            "noticeTitle": {
                "fontSize": 12,
                "bold": true
            },
            "noticeText": {
                "fontSize": 12
            },
            "footer": {
                "color": "#777777",
                "fontSize": 10,
                "alignment": "center"
            }
        },


    };

};

export default dd;